/* -------------------------- Design imports start -------------------------- */
import { Cancel, Close, Edit, Save } from '@mui/icons-material'
import { Grid, IconButton, Typography } from '@mui/material'
import Drawer from '@mui/material/Drawer'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { type ReactElement, useEffect, useRef } from "react";
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------- Module state start ---------------------------- */
// Keep a global count of open InfoPanels to coordinate the CSS variable across routes
const InfoPanelOpenCounter = { count: 0 }
/* --------------------------- Module state end ----------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  allowEdit?: boolean
  onEdit?: () => void
  onSave?: () => void
  onCancel?: () => void
  children: React.ReactNode
  label: string
  disabled: boolean
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function InfoPanel(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    allowEdit = true,
    onEdit,
    onSave,
    onCancel,
    children,
    label,
    disabled,
  } = props
  const drawerWidth = 350
  const drawerTransitionMs = 300
  const drawerTransition = `${drawerTransitionMs}ms ease`
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const infoPanelRef = useRef<ReactElement | null>(null)
  /* ----------------------------- Data states end ---------------------------- */

  /* ---------------------------- Module scoped refs -------------------------- */
  // Track how many InfoPanels are open across the app to avoid race conditions
  // when navigating between pages (cleanups of unmounted panels would otherwise
  // reset the CSS variable back to 0).
  const openRef = useRef(false)
  /* -------------------------- Module scoped refs end ------------------------ */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    const adjustInfoPanelHeight = (): void => {
      const infoPanelElement: ReactElement | null = infoPanelRef.current
      if (infoPanelElement) {
        (infoPanelElement as any).style.height = `${window.innerHeight - (infoPanelElement as any).getBoundingClientRect().top}px`
      }
    }

    adjustInfoPanelHeight()
    window.addEventListener('resize', adjustInfoPanelHeight)

    return () => {
      window.removeEventListener('resize', adjustInfoPanelHeight)
    }
  }, [])

  // Track all open InfoPanels globally to keep the CSS variable in sync across navigation
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const counter = InfoPanelOpenCounter

    // update counter based on open state changes
    if (open && !openRef.current) {
      counter.count += 1
      openRef.current = true
    } else if (!open && openRef.current) {
      counter.count = Math.max(0, counter.count - 1)
      openRef.current = false
    }

    const nextWidth = counter.count > 0 ? `${drawerWidth}px` : '0px'
    root.style.setProperty('--info-panel-width', nextWidth)

    return () => {
      if (openRef.current) {
        counter.count = Math.max(0, counter.count - 1)
        openRef.current = false
        const cleanupWidth = counter.count > 0 ? `${drawerWidth}px` : '0px'
        root.style.setProperty('--info-panel-width', cleanupWidth)
      }
    }
  }, [open, drawerWidth])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Render constants start ------------------------- */
  const list = () => (
    <div ref={infoPanelRef as any} style={{ overflowY: 'auto' }}>
      <Grid style={{ overflowY: 'auto' }} container spacing={1}>
        <Grid item xs={12}>
          <Grid container justifyContent="space-between">
            <Grid item xs={10}>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1, margin: '14px' }}>
                {label}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <IconButton onClick={() => setOpen(false)} sx={{ margin: '5px', marginTop: '10px' }}>
                <Close />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ marginRight: '10px' }}>
          {allowEdit &&
            (!disabled ? (
              <>
                {onSave && (
                  <IconButton sx={{ float: 'right' }} onClick={onSave}>
                    <Save />
                  </IconButton>
                )}
                {onCancel && (
                  <IconButton sx={{ float: 'right' }} onClick={onCancel}>
                    <Cancel />
                  </IconButton>
                )}
              </>
            ) : (
              <>
                {onEdit && (
                  <IconButton sx={{ float: 'right' }} onClick={onEdit}>
                    <Edit />
                  </IconButton>
                )}
              </>
            ))}
        </Grid>
        <Grid style={{ marginBottom: '5px' }} item xs={12}>
          {children}
        </Grid>
      </Grid>
    </div>
  )

  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <div>
      <div>
        <Drawer
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 64,
              transition: `transform ${drawerTransition}, width ${drawerTransition}`,
            },
          }}
          transitionDuration={{ enter: drawerTransitionMs, exit: drawerTransitionMs }}
          SlideProps={{
            timeout: drawerTransitionMs,
            easing: { enter: 'ease', exit: 'ease' },
          }}
          open={open}
          anchor="right"
          onClose={() => setOpen(false)}
          variant="persistent"
          style={{ position: 'relative', zIndex: 1 }}
        >
          {list()}
        </Drawer>
      </div>
    </div>
  )
}
